import styled, { css, keyframes } from 'styled-components'
import { desktopBreakpoint, tabletBreakpoint, wideScreenBreakpoint } from 'react-components'

export const Container = styled.section(
    ({ theme }) => css`
        width: inherit;
        padding: ${theme.spacing.xSmall} 0;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            padding: ${theme.spacing.xLarge} ${theme.spacing.large} ${theme.spacing.medium};
        }

        @media only screen and (min-width: ${wideScreenBreakpoint.min}px) {
            padding: ${theme.spacing.xLarge} 4rem ${theme.spacing.medium};
        }
    `,
)

export const InnerContainer = styled.div(
    ({ theme }) => css`
        margin: 0 ${theme.spacing.medium};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            margin: 0 ${theme.spacing.large};
        }

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            margin: 0 auto;
        }
    `,
)

export { Title } from '../common/styles'

export const ChartTitle = styled.h3(
    ({ theme }) => css`
        margin: ${theme.spacing.large} 0 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: 1.125rem;
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const HeaderRow = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: baseline;
        gap: ${theme.spacing.xSmall};
        margin-top: ${theme.spacing.small};
    `,
)

export const HeaderPercent = styled.span(
    ({ theme }) => css`
        font-size: ${theme.typography.font.size.xLarge};
        font-weight: ${theme.typography.font.lato.weight.bold};
        color: ${theme.colors.tfwYellow};
    `,
)

export const HeaderDates = styled.span(
    ({ theme }) => css`
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1rem;
        color: ${theme.colors.tfwBlack30};

        @media only screen and (min-width: ${tabletBreakpoint.min}px) {
            font-size: ${theme.typography.font.size.small};
            letter-spacing: -0.006em;
            line-height: 1.25rem;
        }
    `,
)

export const HeaderAverage = styled.div(
    ({ theme }) => css`
        margin-top: ${theme.spacing.xxSmall};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        letter-spacing: -0.006em;
        line-height: 1.25rem;
        color: ${theme.colors.tfwBlack38};
    `,
)

export const ChartScroller = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    @media only screen and (min-width: ${desktopBreakpoint.min}px) {
        /* no horizontal scrolling on desktop; visible overflow lets the tooltip float above the chart */
        overflow: visible;
    }
`

export const BarsArea = styled.div(
    ({ theme }) => css`
        position: relative;
        display: flex;
        align-items: flex-end;
        gap: ${theme.spacing.xxxSmall};
        height: 9rem;
        min-width: 34rem;
        margin-top: ${theme.spacing.large};
        padding-bottom: 1.25rem;

        @media only screen and (min-width: ${desktopBreakpoint.min}px) {
            height: 13rem;
            min-width: 0;
        }
    `,
)

export const BarSlot = styled.div`
    position: relative;
    display: flex;
    align-items: flex-end;
    flex: 1 0 0;
    min-width: 0.5rem;
    height: calc(100% - 1.25rem);
`

export const Bar = styled.button.attrs<{ $height: number; $active: boolean }>(({ $height }) => ({
    style: { height: `${$height}%` },
}))<{ $height: number; $active: boolean }>(
    ({ $active, theme }) => css`
        width: 100%;
        min-height: 2px;
        padding: 0;
        border: none;
        border-radius: ${theme.typography.border.radius.xxSmall} ${theme.typography.border.radius.xxSmall} 0 0;
        background-color: ${$active ? theme.colors.tfwYellow : theme.colors.tfwBlack12};
        cursor: pointer;
        transition: background-color 0.15s ease;
    `,
)

export const AverageLine = styled.div<{ $bottom: number }>(
    ({ $bottom, theme }) => css`
        position: absolute;
        left: 0;
        right: 0;
        bottom: calc(1.25rem + (100% - 1.25rem) * ${$bottom / 100});
        border-top: ${theme.typography.border.width.xxThin} ${theme.typography.border.style.dotted}
            ${theme.colors.tfwBlack38};
        pointer-events: none;
    `,
)

export const MonthLabel = styled.span(
    ({ theme }) => css`
        position: absolute;
        top: 100%;
        left: 0;
        margin-top: ${theme.spacing.xxSmall};
        font-size: ${theme.typography.font.size.xSmall};
        color: ${theme.colors.tfwBlack45};
        text-transform: uppercase;
        white-space: nowrap;
    `,
)

export const Tooltip = styled.div.attrs<{
    $align: 'left' | 'center' | 'right'
    $barHeight: number
    $hidden?: boolean
}>(({ theme, $barHeight, $hidden, $align }) => ({
    style: {
        bottom: `min(calc(${$barHeight}% + ${theme.spacing.large}), 100%)`,
        display: $hidden ? 'none' : undefined,
        ...($align === 'center' ? { left: '50%', transform: 'translateX(-50%)' } : { [$align]: 0 }),
    },
}))<{ $align: 'left' | 'center' | 'right'; $barHeight: number; $hidden?: boolean }>(
    ({ theme }) => css`
        position: absolute;
        z-index: 2;

        &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            height: ${theme.spacing.large};
        }
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.xSmall};
        width: max-content;
        min-width: 16rem;
        padding: ${theme.spacing.small};
        border-radius: ${theme.typography.border.radius.large};
        background-color: ${theme.colors.white};
        box-shadow:
            0 12px 32px rgba(0, 0, 0, 0.18),
            0 2px 8px rgba(0, 0, 0, 0.08);
        text-align: left;
    `,
)

export const TooltipPercent = styled.span(
    ({ theme }) => css`
        font-size: ${theme.typography.font.size.large};
        font-weight: ${theme.typography.font.lato.weight.black};
        color: ${theme.colors.tfwBlack87};
    `,
)

export const TooltipValueSuffix = styled.span(
    ({ theme }) => css`
        margin-left: ${theme.spacing.xxSmall};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.regular};
        color: ${theme.colors.tfwBlack87};
    `,
)

export const TooltipLabel = styled.span(
    ({ theme }) => css`
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack87};
        white-space: nowrap;
    `,
)

export const MobileWeekPanel = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing.xSmall};
        margin-top: ${theme.spacing.small};
        padding: ${theme.spacing.small};
        border: ${theme.typography.border.width.xxxThin} ${theme.typography.border.style.solid}
            ${theme.colors.tfwBlack12};
        border-radius: ${theme.typography.border.radius.large};
        background-color: ${theme.colors.white};
    `,
)

export const ChartAxisLabel = styled.div(
    ({ theme }) => css`
        margin-top: ${theme.spacing.small};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack45};
        text-align: center;
    `,
)

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`

export const CtaLabel = styled.span(
    ({ theme }) => css`
        position: relative;

        &[data-loading='true'] {
            color: transparent;
        }

        &[data-loading='true']::after {
            content: '';
            position: absolute;
            inset: 0;
            margin: auto;
            width: 1.25rem;
            height: 1.25rem;
            border: 2px solid ${theme.colors.tfwBlackOpacity10};
            border-top-color: ${theme.colors.tfwBlack87};
            border-radius: 50%;
            animation: ${spin} 0.8s linear infinite;
        }
    `,
)
